import axios from "axios";
import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import { notification } from "antd";
import { refreshTokenApi } from "./authApi";
import { useAuthStore } from "../stores/authStore";
import { useFyStore } from "../stores/fyStore";

const DEFAULT_ORG_ID = Number(import.meta.env.VITE_ORG_ID ?? 1);
const DEFAULT_FY_ID = Number(import.meta.env.VITE_FY_ID ?? 1);
const API_BASE_URL = "/api";

function buildLoginRedirectUrl() {
  if (typeof window === "undefined") {
    return "/login";
  }

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const safeReturnUrl = currentPath.startsWith("/") ? currentPath : "/";

  if (safeReturnUrl === "/login") {
    return "/login";
  }

  return `/login?returnUrl=${encodeURIComponent(safeReturnUrl)}`;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000
});

let refreshingPromise: Promise<string | null> | null = null;

apiClient.interceptors.request.use((config) => {
  const state = useAuthStore.getState();
  const token = state.accessToken;
  const fyState = useFyStore.getState();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  config.headers["X-Org-Id"] = String(DEFAULT_ORG_ID);

  // Send both FY id and FY code so backend context resolution stays stable
  // even before a refreshed token carries the latest financial-year claim.
  if (fyState.activeFyId) {
    config.headers["X-FY-ID"] = String(fyState.activeFyId);
  }

  const fyCode = fyState.activeFyCode;
  if (fyCode) {
    config.headers["X-FY"] = fyCode;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;

    if (status === 403) {
      const url = String(originalRequest?.url ?? "");
      notification.error({
        message: "Access Denied",
        description: `You don't have permission to perform this action. Contact your administrator to grant the required permission for: ${url || "this resource"}.`,
        duration: 6,
        key: "permission-denied"
      });
      return Promise.reject(error);
    }

    if (!originalRequest || status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const requestUrl = String(originalRequest.url ?? "").toLowerCase();
    const isAuthEndpoint =
      requestUrl.includes("/auth/login")
      || requestUrl.includes("/auth/loginpayload")
      || requestUrl.includes("/auth/refresh");
    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    const state = useAuthStore.getState();
    if (!state.refreshToken) {
      state.clearSession();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.replace(buildLoginRedirectUrl());
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!refreshingPromise) {
      refreshingPromise = (async () => {
        try {
          const refreshed = await refreshTokenApi(state.refreshToken!);
          useAuthStore.getState().setTokens({
            accessToken: refreshed.accessToken,
            refreshToken: refreshed.refreshToken,
            accessTokenExpiresOnUtc: refreshed.accessTokenExpiresAt,
            refreshTokenExpiresOnUtc: null
          });
          return refreshed.accessToken;
        } catch {
          useAuthStore.getState().clearSession();
          if (typeof window !== "undefined" && window.location.pathname !== "/login") {
            window.location.replace(buildLoginRedirectUrl());
          }
          return null;
        } finally {
          refreshingPromise = null;
        }
      })();
    }

    const newAccessToken = await refreshingPromise;
    if (!newAccessToken) {
      return Promise.reject(error);
    }

    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
    return apiClient(originalRequest);
  }
);

export function getDefaultContext() {
  const fyId = useFyStore.getState().activeFyId;
  return {
    organizationId: DEFAULT_ORG_ID,
    financialYearId: fyId || DEFAULT_FY_ID
  };
}
